import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { TranslocoService } from '@ngneat/transloco'
import { signInWithCustomToken } from 'firebase/auth'
import kebabCase from 'lodash-es/kebabCase'
import { NgxSpinnerService } from 'ngx-spinner'
import { first } from 'rxjs/internal/operators/first'
import { BehaviorSubject } from 'rxjs'
import { ApiService } from './api.service'
import { AuthService } from './auth.service'
import { Cry } from './cry.service'
import { EmailService } from './email.service'
import { FirestoreService2 } from './firestore.service2'
import { GlobalService } from './global.service'
import { NotifyService } from './notifications/notify.service'
import { UserService } from './user.service'
import { timeOut, use } from './utils'

let env: any
let BUILD: any

export interface IGUIAConfig {
  issuer: string
  redirectUri: string
  clientId: string
  secret: string
  responseType: string
  scope: string,
  segmentField: string,
  segmentFromGuia: boolean,
  segmentPrefix: string,
  notifyManagerOnNewUser: boolean,
  notifyManagerOnLoginError: boolean,
  notifyManagerOnLoginErrorEmail: string
}

@Injectable({
  providedIn: 'root'
})
export class AuthGuiaService {

  private authCodeFlowConfig: any = null
  private segmentFromGuia = false
  private segmentField = ''
  private segmentPrefix = ''
  private accessSegmentsFromGuia = false
  private notifyManagerOnNewUser = false
  private notifyManagerOnLoginError = false
  private notifyManagerOnLoginErrorEmail: string
  private _config: any = null
  private loadedNick: string | null = null
  /** Emits whether GUIA is configured for the current program. */
  readonly configured$ = new BehaviorSubject<boolean>(false)
  private fss: FirestoreService2

  constructor(
    private auth: AuthService,
    private router: Router,
    private notify: NotifyService,
    private g: GlobalService,
    private transloco: TranslocoService,
    private spiner: NgxSpinnerService,
    private userService: UserService
  ) {
    env = (window as any).y4wenvs.ENV
    BUILD = (window as any).y4wenvs.BUILD
    void this.config()
    // The program (and its nick) may load after this service is constructed.
    // Re-run the config fetch when the nick becomes available or changes.
    this.g.get$('nick').subscribe((nick: string) => {
      if (nick) void this.config()
    })
  }

  async config(): Promise<void> {
    const nick = this.g.nick
    if (!nick) return
    if (this.loadedNick === nick && this._config) return

    const api = use<ApiService>(ApiService)
    const config: Partial<IGUIAConfig> = await api.post('open/firestoreAction', {
      action: 'guiaConfig',
      nick
    }, true).pipe(first()).toPromise()

    if (!config || !config.issuer) {
      this._config = null
      this.loadedNick = nick
      this.configured$.next(false)
      console.warn('[GUIA] no guiaConfig for nick', nick)
      return
    }

    this.segmentFromGuia = config.segmentFromGuia || false
    this.segmentField = config.segmentField || ''
    this.segmentPrefix = config.segmentPrefix || ''
    this.accessSegmentsFromGuia = !!(config.segmentPrefix && config.segmentPrefix !== '')
    this.notifyManagerOnNewUser = config.notifyManagerOnNewUser || false
    this.notifyManagerOnLoginError = config.notifyManagerOnLoginError || false
    this.notifyManagerOnLoginErrorEmail = config.notifyManagerOnLoginErrorEmail || undefined
    this.authCodeFlowConfig = {
      issuer: config.issuer,
      redirectUri: config.redirectUri,
      clientId: config.clientId,
      responseType: config.responseType || 'code',
      scope: config.scope || 'openid profile team email department',
      showDebugInformation: true,
      timeoutFactor: 0.01,
    }
    this._config = config
    this.loadedNick = nick
    this.configured$.next(true)
  }

  /** True when the current program has a usable GUIA configuration. */
  get isConfigured(): boolean {
    return !!(this._config && this.authCodeFlowConfig?.issuer)
  }

  async login(): Promise<void> {
    if (!this.isConfigured) await this.config()
    if (!this.isConfigured) {
      const key = 'guia_not_configured'
      const message = this.transloco.translate(key)
      this.notify.update(message === key ? 'O login GUIA não está configurado para este programa.' : message, 'btn-danger', 5000)
      return
    }
    this.spiner.show()
    window.location.href = encodeURI(`${this.authCodeFlowConfig.issuer}/authorize?client_id=${this.authCodeFlowConfig.clientId}&redirect_uri=${this.authCodeFlowConfig.redirectUri}&response_type=code&scope=${this.authCodeFlowConfig.scope}`)
  }

  requestToken(code: string) {
    this.saveDebug({ message: `# requestToken START `, stack: JSON.stringify(code) })
    this.spiner.show()
    const api: ApiService = use<ApiService>(ApiService)
    const fss: FirestoreService2 = use<FirestoreService2>(FirestoreService2)
    fss.toPromise(api.post('users/userAction?noAuthRequest=true', {
      action: 'validateOidToken',
      nick: this.g.nick,
      code
    }, true)).then(ok => {
      if (ok.success) {
        this.saveDebug({ message: `# requestToken validateOidToken `, stack: JSON.stringify(ok.data.user || {}) })
        this.validateOpenIDUserToken(ok.data.user).then(u => {
          timeOut(async () => {
            this.saveLastLogin()
            await this.userService.recalcBalance({ userCode: ok.data.user.sub, email: ok.data.user.email, displayName: ok.data.user.name, id: null, nick: this.g.nick }).then(() => null)
            this.auth.redirectUser(u)
            await this.spiner.hide()
          }, 500)
        }).catch(e => {
          this.auth.signOut()
          if ((e.message || e) === 'guia_auth_error_missing_email') {
            this.notify.alert(this.transloco.translate('guia_auth_error_missing_email'), `
          <span>${this.transloco.translate('guia_register_email_instructions')}</span>
          <ol>
          <li>${this.transloco.translate('guia_register_step_1')}</li>
          <li>${this.transloco.translate('guia_register_step_2')}</li>
          <li>${this.transloco.translate('guia_register_step_3')}</li>
          <li>${this.transloco.translate('guia_register_step_4', { program: this.g.program.name })}</li>
          <li>${this.transloco.translate('guia_register_step_5')}</li>
          <li>${this.transloco.translate('guia_register_step_6')}</li>
          <li>${this.transloco.translate('guia_register_step_7')}</li>
          <li>${this.transloco.translate('guia_register_step_8', { program: this.g.program.name })}</li>
          <li>${this.transloco.translate('guia_register_step_9')}</li>
          <li>${this.transloco.translate('guia_register_step_10')}</li>
          </ol>
          <span>${this.transloco.translate('guia_register_help_text')}</span>
          `, 'OK', 'lg', { width: '100%', 'max-width': '450px' })
          }
          timeOut(() => this.notify.update(this.transloco.translate(e.message || e), 'btn-danger', 7000), 250)
          this.spiner.hide()
        })
      } else {
        this.saveDebug({ message: `# requestToken ERROR 1 `, stack: JSON.stringify(ok || {}) })
        this.spiner.hide()
        timeOut(() => this.notify.update(this.transloco.translate(ok.error.error || ok.message || 'guia_request_token_error'), 'btn-danger', 7000), 250)
        this.router.navigate(['/login'])
      }
    }, (e: any) => {
      this.spiner.hide()
      this.saveDebug({ message: `# requestToken ERROR 2 `, stack: JSON.stringify(e) })
      timeOut(() => this.notify.update(this.transloco.translate('guia_request_token_error'), 'btn-danger', 7000), 250)
      this.router.navigate(['/login'])
    })
  }

  validateOpenIDUserToken(oidUser: any) {
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)
    if (!oidUser || !oidUser.name || !oidUser.role || !oidUser.role.length || !oidUser.sub || (this.segmentFromGuia && this.segmentField && !oidUser[this.segmentField])) {
      this.saveDebug({ message: `# guia_auth_error_missing_params `, stack: JSON.stringify(oidUser) })
      this.notifyManagerLoginError(oidUser)
      return Promise.reject('guia_auth_error_missing_params')
    }
    if (!oidUser.email) {
      this.saveDebug({ message: `# guia_auth_error_missing_email `, stack: JSON.stringify(oidUser) })
      return Promise.reject('guia_auth_error_missing_email')
    }
    const api: ApiService = use<ApiService>(ApiService)
    oidUser.email = oidUser.email.toLowerCase()
    return new Promise((resolve, reject) => {
      api.post('users/userAction?noAuthRequest=true', {
        action: 'validateToken',
        uid: btoa(oidUser.email + oidUser.email.split('@')[1]).replace(/=/g, ''),
        email: oidUser.email
      }, true).pipe(first()).toPromise().then(ok => {
        if (ok.success) {
          this.saveDebug({ message: `# validateToken `, stack: ok.token })
          this.authWithOpenIDFirebase(ok.token, oidUser)
            .then(async (user: any) => {
              if (!user.error) {
                // migrates users from before GUIA
                await api.post('users/migrateEmailToProvider', {
                  uid: user.uid,
                  email: oidUser.email,
                  userCode: oidUser.sub,
                  nick: this.g.nick,
                }, true).pipe(first()).toPromise().then(_ok => null)

                const updatedUser = await this.fss.get('users', user.uid, { api: true }).pipe(first()).toPromise()

                if (updatedUser) this.notifyManager(updatedUser)
                this.saveDebug({ message: `# authWithOpenIDCredential `, stack: JSON.stringify(user) })
                api.post('users/updateUserAuth', { uid: user.uid, displayName: oidUser.name || '', email: oidUser.email, nick: this.g.nick }, true)
                  .pipe(first()).toPromise()
                  .then(ok => {
                    if (ok && (ok.success === false || ok.error)) reject(ok.message || ok.error)
                    else {
                      user.firstLogin = user.firstLogin || new Date().toISOString()
                      user.lastLogin = new Date().toISOString()
                      this.fss.update('users', user.uid || user.id, { createdAt: user.createdAt, firstLogin: user.firstLogin, lastLogin: user.lastLogin, email: oidUser.email }, { silent: true }).then()
                      resolve(user)
                    }
                  })
                  .catch(_er => {
                    console.log('#updateUserAuth', _er.message)
                    this.notify.update(this.transloco.translate(_er.message), 'btn-danger', 6000)
                    reject(_er.message)
                  })
              } else {
                this.saveDebug({ message: `# authOpenIDWithFirebase `, stack: JSON.stringify(user) })
                this.notify.update(this.transloco.translate(user.error), 'btn-danger', 6000)
                resolve(user)
              }

            })
            .catch(async (error) => {
              this.saveDebug({ ...error, message: `# authWithOpenIDFirebase ${error.message}` })
              this.notify.update(error.message, 'btn-danger', 3000)
              reject(error)
            })
        } else if (ok.error) {
          this.notify.update(ok.error, 'btn-danger', 3000)
          reject(ok.error)
        }
      }).catch(err => {
        this.saveDebug({ ...err, message: `# validateToken ERROR ` + err.message })
      })
    })
  }

  authWithOpenIDFirebase(token: string, oidUser: any) {
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)
    const api = use<ApiService>(ApiService)
    return signInWithCustomToken(this.auth.afAuth, token)
      .then(async (result) => {
        if (!result || !result.user) {
          return { error: 'firebase_signin_failed' }
        }
        oidUser.email = oidUser.email.toLowerCase().trim()
        let dbUser = await this.fss.get('users', result.user.uid, { api: true }).pipe(first()).toPromise()
        let prospect = null

        // verify user existing in DB
        if (!dbUser) {
          let role = 'user'
          if (oidUser.role && typeof oidUser.role === 'string') role = oidUser.role.toLowerCase()
          else if (oidUser.role && Array.isArray(oidUser.role)) {
            if (oidUser.role.includes('manager')) role = 'manager'
          }

          const newUser: any = {
            active: true,
            addresses: [],
            approve: !this.notifyManagerOnNewUser,
            balance: 0,
            balanceEuros: 0,
            cart: [],
            cartEuros: [],
            createdAt: new Date().toISOString(),
            department: oidUser.department || null,
            displayName: oidUser.name || oidUser.email.split()[0] || '',
            email: oidUser.email,
            emailVerified: result.user.emailVerified || true,
            firstLogin: new Date().toISOString(),
            firstName: (oidUser.name || '').split(' ')[0],
            guiaMigration: false,
            hasGuiaMigrated: true,
            id: result.user.uid,
            isAnonymous: result.user.isAnonymous || false,
            lastLogin: new Date().toISOString(),
            name: oidUser.name || '',
            nick: this.g.nick,
            photo: result.user.photoURL || null,
            provider: 'OpenID',
            providerId: oidUser.sub || '',
            role: role,
            segment: null,
            segments: [],
            team: oidUser.team || null,
            uid: result.user.uid,
            updatedAt: new Date().toISOString(),
            userCode: oidUser.sub || undefined,
            wishlist: [],
          }

          // verify if program lookups
          if (this.g.program.logins.lookup) {
            prospect = await api.post('db/action', {
              action: 'findProspect',
              nick: this.g.nick,
              email: oidUser.email.toLowerCase()
            }, true)
            if (prospect && prospect.active && !prospect.used) {
              newUser.segment = prospect.segment || null
              newUser.segments = prospect.segments || []
              newUser.displayName = newUser.displayName || prospect.displayName || ''
              newUser.role = prospect.role || 'user'

              // creates the user via cripted API
              await api.post('db/action', {
                action: 'createUser',
                id: result.user.uid,
                nick: this.g.nick,
                data: newUser
              }, true).pipe(first()).toPromise().then(() => {
                this.fss.merge('prospects', prospect.id, { used: true })
              })
            } else return { error: 'user_not_allowed_or_has_registed' }
          } else {
            // creates the user via cripted API
            this.saveDebug({ message: `# createUser `, stack: JSON.stringify(newUser) })
            await api.post('db/action', {
              action: 'createUser',
              id: result.user.uid,
              nick: this.g.nick,
              data: newUser
            }, true).pipe(first()).toPromise().then(() => {
              dbUser = newUser
            })
          }

        } else if (!dbUser.active) {
          this.auth.signOut(false)
          return { error: 'user_not_registed_or_not_activated' }
        }

        let segment = await this.getSegmentFromGuia(oidUser)
        const accessSegmentsResult = await this.getAccessSegmentsFromGuia(oidUser)

        if (!segment && accessSegmentsResult.segment) segment = accessSegmentsResult.segment

        await this.updateUserFromGuiaData(dbUser, oidUser, segment, accessSegmentsResult)
        return { ...result.user, ...dbUser }
      })
      .catch((error) => {
        this.saveDebug({ message: `# authWithOpenIDCredential `, stack: JSON.stringify(error.stack) })
        console.log('#error', error)
        return { error }
      })
  }

  async getSegmentFromGuia(oidUser: any) {
    if (!this.segmentFromGuia) return null

    const _segments = await this.fss.list('segments', { api: true }).pipe(first()).toPromise()
    let segment = null

    if (oidUser.role && typeof oidUser.role === 'string' && oidUser[this.segmentField]) {
      segment = _segments.find(s => s.name.toLowerCase() === oidUser[this.segmentField].toLowerCase()) || null
      if (!segment) segment = await this.saveNewSegment(oidUser[this.segmentField])
    }

    return segment
  }

  async getAccessSegmentsFromGuia(oidUser: any) {
    if (!this.accessSegmentsFromGuia) return { segment: null, segments: [] }

    const _segments = await this.fss.list('segments', { api: true }).pipe(first()).toPromise()
    const segmentsMapping = await this.fss.list('settings', { subcol: 'segmentsMapping' }).pipe(first()).toPromise()
    let segment = null
    let segments = []

    if (oidUser.role && Array.isArray(oidUser.role)) {
      if (segmentsMapping && segmentsMapping.length) {
        for (const map of segmentsMapping) {
          if (oidUser.role.filter((r: any) => r.indexOf(map.code) > -1).length && map.segment) {
            const segmentId = map.segment.id || map.segment
            if (!segments.includes(segmentId)) segments.push(segmentId)
          }
        }
      }
      const accessSegments = oidUser.role.filter((r: any) => r.indexOf(this.segmentPrefix) > -1)

      if (accessSegments && accessSegments.length) {
        for (let i = 0; i < accessSegments.length; i++) {
          const _segment = accessSegments[i].replace(this.segmentPrefix, '') // removes the segment prefix
          let dbSegment = _segments.find(s => s.name.toLowerCase() === _segment.toLowerCase()) || null
          if (!dbSegment) dbSegment = await this.saveNewSegment(_segment)
          if (i === 0) segment = dbSegment // First accessSegment is the main segment
          else if (!segments.includes(dbSegment.id)) segments.push(dbSegment.id)
        }
      }
    }

    return { segment: segment, segments: segments }
  }

  async saveNewSegment(segmentName: string) {
    const segment = {
      id: this.fss.createId,
      active: true,
      hasRank: true,
      name: segmentName,
      uniq: kebabCase(segmentName),
      nick: this.g.nick
    }
    await this.fss.set('segments', segment.id, segment).then()

    return segment
  }

  async updateUserFromGuiaData(dbUser: any, oidUser: any, segment: any, accessSegmentsResult: any) {
    if (!dbUser) return
    const updates: any = {}
    // check segment
    if (segment) {
      if (!dbUser.segment) updates.segment = segment
      else if (dbUser.segment.id && dbUser.segment.id !== segment.id) updates.segment = segment
    }
    // check segments array
    if (accessSegmentsResult.segments) {
      if (dbUser.segments && !dbUser.segments.length && accessSegmentsResult.segments.length) {
        updates.segments = accessSegmentsResult.segments
      } else if (accessSegmentsResult.segments.length || dbUser.segments.length) {
        updates.segments = accessSegmentsResult.segments
      }
    }

    if (oidUser.department && dbUser.department !== oidUser.department) updates.department = oidUser.department
    if (oidUser.team && dbUser.team !== oidUser.team) updates.team = oidUser.team
    // check role
    if (oidUser && oidUser.role && dbUser.role) {
      const roleUpdate = this.getRoleUpdate(dbUser, oidUser)
      if (roleUpdate) updates.role = roleUpdate
    }
    // single update if there are changes
    if (Object.keys(updates).length > 0) {
      updates.createdAt = dbUser.createdAt
      await this.fss.update('users', dbUser.uid || dbUser.id, updates, { silent: true })
    }
  }

  getRoleUpdate(dbUser: any, oidUser: any): string | null {
    if (oidUser.role && Array.isArray(oidUser.role) && (!oidUser.role.map(r => r.toLowerCase()).includes(dbUser.role.toLowerCase()) || oidUser.role.includes('manager'))) {
      return oidUser.role.includes('manager') ? 'manager' : 'user'
    } else if (oidUser.role && typeof oidUser.role === 'string' && dbUser.role.toLowerCase() !== oidUser.role.toLowerCase()) {
      return oidUser.role.toLowerCase()
    }
    return null
  }

  saveDebug(error: any) {
    const user = this.g.get('user')
    const err = {
      error: {
        message: error.message,
        stack: error.stack || '',
        name: error.name || ''
      },
      path: window.location.href,
      user: user ? user.email : 'indefinido',
      nick: this.g.nick,
      timestamp: new Date().getTime(),
      createdAt: new Date().toISOString(),
      build: BUILD
    }
    if (!env.production) console.log(error.message)

    const api = use<ApiService>(ApiService)
    api.post(`users/userAction?noAuthRequest`, { action: 'debug', err }, true).pipe(first()).toPromise()
  }

  notifyManager(newUser: any) {
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)
    const email = use<EmailService>(EmailService)
    setTimeout(async () => {
      const user = await this.fss.get('users', newUser.id || newUser.uid, { api: true }).pipe(first()).toPromise()
      if (user && this.notifyManagerOnNewUser && (!user.approve || !user.segment)) {
        console.log('# notifyManager')
        email.sendPlain({
          subject: 'Novo utilizador a aprovar em ' + this.g.program.name,
          name: this.g.program.name + ' Manager',
          user: this.g.program.name + ' Manager',
          email: this.g.program.manager,
          bcc: 'waltergandarella@yesmkt.com',
          message: `
      Há um novo utilizador registado que necessita de sua aprovação:<br><br>
      <strong>${newUser.displayName || newUser.name || 'Nome não definido'} - ${newUser.userCode} - ${newUser.email}</strong><br><br>
      Por favor, vai até ${this.g.program.url}/admin/tables/user-approval para aprovar novos utilizadores.
      `,
          programImg: this.g.program.images.logoMobile
        }, email.emailProgram()).subscribe(() => console.log('OK'))
      }
    }, 1000 * 60)
  }

  notifyManagerLoginError(oidUser: any) {
    if (this.notifyManagerOnLoginError && this.notifyManagerOnLoginErrorEmail) {
      try {
        const email = use<EmailService>(EmailService)
        timeOut(async () => {
          console.log('# notifyManager')
          let message = `
          O GUIA negou acesso a este utilizador:<br><br>
          <strong>Nome:</strong> ${oidUser.name || ''}<br>
          <strong>User:</strong> ${oidUser.sub || ''}<br>
          <strong>Email:</strong> ${oidUser.email || ''}<br>
          <strong>role:</strong> ${oidUser.role || ''}<br>
        `
          if (this.segmentFromGuia && !oidUser[this.segmentField]) message += `<strong>${this.segmentField}</strong>: ${oidUser[this.segmentField] || ''}<br><br>`

          email.sendPlain({
            subject: 'GUIA não deu acesso ao utilizador ' + oidUser.sub || '',
            name: this.g.program.name + ' Manager',
            user: this.g.program.name + ' Manager',
            email: this.notifyManagerOnLoginErrorEmail,
            bcc: 'waltergandarella@yesmkt.com',
            message,
            programImg: this.g.program.images.logoMobile
          }, email.emailProgram()).subscribe(() => console.log('OK'))
        }, 1000 * 60)
      } catch (e) {
        // avoids the error
      }
    }
  }

  saveLastLogin() {
    Cry.setLocal('lastLogin', Date.now(), true)
  }
}

// Node SAML
// https://github.com/node-saml/node-saml

// OneLogin clientID: 7bb00f70-a94e-013a-6cd5-0ad5aca7c98b208833
// OneLogin secret: 2b7db0e8af2092a267fa49245694d8263c964b4f1ed5688df7968f032ec1f82d

// API
// clientID: 677d01bfe971a5e4bc051860a0e9423dc58f33e453d0a8fa6b9cddf8025074b1
// secret: 1fc65410cd621434e59522772bd38c711ab5ffc077706e5f44bd9abf650d2a1c

// user teste NOS Tecmais
// username: wgneto
// pass: Tecmais2022!
// issuer: https://authdev.co.nos.pt/auth?client_id=Tecmais&redirect_uri=http%3A%2F%2Flocalhost%3A4500%2Flogin%2Ftoken%2Foid&response_type=code&scope=openid%2Bprofile%2Bemail
// curl -X POST -d grant_type=authorization_code -d 'redirect_uri=http://localhost:4500/login/token/oid' -d code=80ca2d9a75a453d56722fcf4ec639f488f68f4aa2dabc9e3c978815862516582 -u 'tecmais:Teste123!' 'https://authdev.co.nos.pt/oauth2/token'
// login https://authdev.co.nos.pt/oauth2/authorize?client_id=tecmais&redirect_uri=https://tecmais.yesmktg.net/login/token/oid&response_type=code&scope=openid%20profile%20email
